import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Phone, Upload, FileText, X, CalendarCheck, Loader2, Bell, BellRing, MapPin, Calendar, Clock, ZoomIn, ZoomOut, Megaphone, Share2, KeyRound, Facebook, Instagram, Youtube, Link2, PlayCircle, MessageCircle, Crop } from "lucide-react";
import PhotoCropDialog from "@/components/idc/PhotoCropDialog";
import { downscaleImage, enablePushForMobile } from "@/lib/eventLib";
import PassDownloadDialog from "@/components/events/PassDownloadDialog";
import { ContactLinks } from "@/components/events/ContactNumbers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_META = {
  facebook: { Icon: Facebook, label: "Facebook", cls: "text-blue-600 bg-blue-50 border-blue-200" },
  instagram: { Icon: Instagram, label: "Instagram", cls: "text-pink-600 bg-pink-50 border-pink-200" },
  youtube: { Icon: Youtube, label: "YouTube", cls: "text-red-600 bg-red-50 border-red-200" },
  whatsapp: { Icon: MessageCircle, label: "WhatsApp", cls: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  other: { Icon: Link2, label: "Link", cls: "text-slate-600 bg-slate-50 border-slate-200" },
};

export default function PublicEventForm({ token }) {
  const [ev, setEv] = useState(null);
  const [err, setErr] = useState("");
  const [subId, setSubId] = useState("");
  const [data, setData] = useState({});
  const [photoCache, setPhotoCache] = useState({});
  const [cropState, setCropState] = useState(null);
  const [uploading, setUploading] = useState({});
  const [catId, setCatId] = useState("");
  const [payRef, setPayRef] = useState("");
  const [payShot, setPayShot] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [zoomImg, setZoomImg] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [descOpen, setDescOpen] = useState(false);
  const [myKey, setMyKey] = useState("");
  const [passDlOpen, setPassDlOpen] = useState(false);

  const enableNotif = async (silent, sid) => {
    setPushBusy(true);
    try {
      await enablePushForMobile(API, token, sid || subId);
      setPushOn(true);
      if (!silent) toast.success("Notification ON — Approve hote hi aapke phone par alert aayega");
    } catch (e) {
      if (!silent) toast.error(e.message || "Notification enable nahi ho paya");
    } finally {
      setPushBusy(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/public/event/${token}`)
      .then((r) => setEv(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Event link not found"));
  }, [token]);

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const shareEvent = async () => {
    const url = window.location.href;
    const text = `${ev.name}${ev.city ? " — " + ev.city : ""}${ev.event_date ? " | " + ev.event_date : ""}\nRegistration form: ${url}`;
    if (navigator.share) {
      try { await navigator.share({ title: ev.name, text, url }); return; } catch { /* user cancelled ya unsupported */ }
    }
    try { await navigator.clipboard.writeText(text); toast.success("Event link copy ho gaya — kahin bhi share karein"); }
    catch { toast.error("Share nahi ho paya"); }
  };

  const pickPhoto = async (file, key) => {
    if (!file) return;
    try { setCropState({ key, src: await downscaleImage(file, 1600, 0.85) }); }
    catch { toast.error("Photo load nahi hui"); }
  };

  const onCropDone = (durl) => {
    const { key } = cropState;
    setCropState(null);
    uploadPhoto(durl, key);
  };

  const uploadPhoto = async (fileOrUrl, key) => {
    if (!fileOrUrl) return;
    setUploading((u) => ({ ...u, [key]: true }));
    try {
      const durl = typeof fileOrUrl === "string" ? fileOrUrl : await downscaleImage(fileOrUrl, 800, 0.8);
      const r = await axios.post(`${API}/public/event/${token}/photos`, { data: durl });
      if (key === "__pay") { setPayShot(r.data.id); setPhotoCache((c) => ({ ...c, [r.data.id]: durl })); }
      else { setField(key, r.data.id); setPhotoCache((c) => ({ ...c, [r.data.id]: durl })); }
      toast.success(key === "__pay" ? "Payment screenshot upload ho gaya" : "Photo upload ho gayi");
    } catch { toast.error("Photo upload failed, dobara try karein"); }
    setUploading((u) => ({ ...u, [key]: false }));
  };

  const selectedCat = (ev?.payment_categories || []).find((c) => c.id === catId);
  const regOpen = ev?.registration_open !== false;

  const submit = async () => {
    if (Object.values(uploading).some(Boolean)) return toast.error("Photo upload ho rahi hai, thoda ruko...");
    for (const f of ev.fields || []) {
      if (f.required && (!data[f.key] || (Array.isArray(data[f.key]) && data[f.key].length === 0))) return toast.error(`${f.label} zaroori hai`);
    }
    if (ev.payment_required && (ev.payment_categories || []).length > 0 && !catId) return toast.error("Payment category chunein");
    if (ev.payment_required && !payShot) return toast.error("Payment ka screenshot upload kiye bina form submit nahi hoga");
    if (ev.pass_download_enabled && !(myKey.length === 4 || myKey.length === 6)) return toast.error("Pass Key banana zaroori hai (4 ya 6 digit)");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/public/event/${token}/submit`, {
        data: Object.fromEntries(Object.entries(data).map(([k, v]) => [k, Array.isArray(v) ? v.join(", ") : v])),
        payment_category_id: catId,
        payment_amount: selectedCat?.amount || 0,
        payment_ref: payRef,
        payment_screenshot: payShot,
        pass_key: ev.pass_download_enabled ? myKey : "",
      });
      setSubId(r.data.id);
      setDone({ status: "pending" });
      enableNotif(true, r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    } finally {
      setBusy(false);
    }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50"><div className="text-center"><img src="/logo.webp?v=2" alt="" className="h-10 mx-auto mb-3" /><p className="text-red-500 font-semibold" data-testid="event-error">{err}</p></div></div>;
  if (!ev) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cyan-600" /></div>;

  if (done) {
    const approved = done.status === "approved";
    return (
      <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="event-done">
        <div className="max-w-lg mx-auto bg-white border border-border rounded-2xl shadow-sm p-8 text-center space-y-3">
          {approved ? <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-500" /> : <CalendarCheck className="h-14 w-14 mx-auto text-amber-500" />}
          <h1 className="font-heading text-xl font-bold" data-testid="event-done-title">{approved ? "Submission Approved \u2705" : "Form Submit Ho Gaya!"}</h1>
          <p className="text-sm text-muted-foreground">
            {approved
              ? `Aapki submission "${done.event_name || ev.name}" ke liye approve ho chuki hai.`
              : "Aapka form receive ho gaya hai. Approve hote hi aapko WhatsApp par jankari share ki jayegi."}
          </p>
          {ev.contact_number && (
            <ContactLinks value={ev.contact_number} prefix="Query? Call" iconClass="h-4 w-4" className="text-cyan-700 font-semibold text-sm justify-center" testId="event-done-contact" />
          )}
          {!approved && (
            <div className="pt-2 border-t mt-2">
              {pushOn ? (
                <p className="inline-flex items-center gap-2 text-emerald-600 font-semibold text-sm" data-testid="event-push-on"><BellRing className="h-4 w-4" /> Notification ON hai — Approve hote hi alert aayega</p>
              ) : (
                <Button onClick={() => enableNotif(false)} disabled={pushBusy} variant="outline" className="gap-2" data-testid="event-push-btn">
                  <Bell className="h-4 w-4" /> {pushBusy ? "Enable ho raha hai..." : "Approval ka Notification ON karein"}
                </Button>
              )}
              <p className="text-[11px] text-muted-foreground mt-2">Isse approve hote hi aapke phone par turant popup aa jayega.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4" data-testid="public-event-form">
      <div className="max-w-lg mx-auto space-y-4">
        {ev.banner && <img src={ev.banner} alt={ev.name} className="w-full aspect-[1200/630] object-cover rounded-2xl border shadow-sm" data-testid="event-banner" />}
        <div className="bg-white border border-border rounded-2xl shadow-sm p-6 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <h1 className="font-heading text-xl font-bold flex-1" data-testid="event-title">{ev.name}</h1>
            <button onClick={shareEvent} title="Event share karein" className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-3 py-1.5 hover:bg-cyan-100 transition-colors" data-testid="event-share-btn"><Share2 className="h-3.5 w-3.5" /> Share</button>
          </div>
          {ev.description && (
            <div data-testid="event-desc">
              <p className={`text-sm text-muted-foreground whitespace-pre-wrap ${ev.description.length > 180 && !descOpen ? "line-clamp-3" : ""}`}>{ev.description}</p>
              {ev.description.length > 180 && (
                <button onClick={() => setDescOpen((o) => !o)} className="text-xs font-semibold text-cyan-700 mt-1" data-testid="event-desc-toggle">{descOpen ? "Minimize \u25B2" : "Read more \u25BC"}</button>
              )}
            </div>
          )}
          {(ev.city || ev.event_date || ev.event_time) && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600" data-testid="event-meta">
              {ev.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-pink-600" /> {ev.city}</span>}
              {ev.event_date && <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5 text-pink-600" /> {ev.event_date}</span>}
              {ev.event_time && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-pink-600" /> {ev.event_time}</span>}
            </div>
          )}
          {(ev.pics || []).length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1">{ev.pics.map((p, i) => <img key={i} src={p} alt="" onClick={() => { setZoomImg(p); setZoom(1); }} className="h-24 rounded-lg border shrink-0 cursor-zoom-in hover:opacity-90 transition-opacity" data-testid={`event-pic-${i}`} />)}</div>
          )}
          {ev.document && (
            <a href={ev.document} download={ev.document_name || "document"} className="inline-flex items-center gap-2 text-sm text-cyan-700 font-semibold" data-testid="event-doc-link"><FileText className="h-4 w-4" /> {ev.document_name || "Download Document"}</a>
          )}
          {ev.contact_number && <ContactLinks value={ev.contact_number} prefix="Contact:" className="text-xs text-muted-foreground" />}
          {ev.pass_download_enabled && (
            <Button onClick={() => setPassDlOpen(true)} variant="outline" className="w-full gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid="event-download-pass-btn">
              <KeyRound className="h-4 w-4" /> Download Your Pass
            </Button>
          )}
        </div>

        {(ev.announcement_text || ev.announcement_image || ev.announcement_doc) && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl shadow-sm p-5 space-y-3" data-testid="event-announcement">
            <p className="font-heading font-bold text-sm text-amber-800 flex items-center gap-1.5"><Megaphone className="h-4 w-4" /> Announcement</p>
            {ev.announcement_text && <p className="text-sm text-amber-900 whitespace-pre-wrap" data-testid="event-ann-text">{ev.announcement_text}</p>}
            {ev.announcement_image && <img src={ev.announcement_image} alt="Announcement" onClick={() => { setZoomImg(ev.announcement_image); setZoom(1); }} className="w-full rounded-lg border cursor-zoom-in hover:opacity-90 transition-opacity" data-testid="event-ann-image" />}
            {ev.announcement_doc && <a href={ev.announcement_doc} download={ev.announcement_doc_name || "announcement"} className="inline-flex items-center gap-2 text-sm text-amber-800 font-semibold" data-testid="event-ann-doc"><FileText className="h-4 w-4" /> {ev.announcement_doc_name || "Download File"}</a>}
          </div>
        )}

        {((ev.social_links || []).some((s) => s.url) || (ev.videos || []).some((v) => v.url)) && (
          <div className="bg-white border border-border rounded-2xl shadow-sm p-5 space-y-3" data-testid="event-social">
            <p className="font-heading font-bold text-sm flex items-center gap-1.5"><Share2 className="h-4 w-4 text-sky-600" /> Social Links &amp; Videos</p>
            {(ev.social_links || []).filter((s) => s.url).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {(ev.social_links || []).filter((s) => s.url).map((s, i) => {
                  const meta = SOCIAL_META[s.type] || SOCIAL_META.other;
                  return (
                    <a key={i} href={s.url} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-1.5 text-xs font-semibold border rounded-full px-3 py-1.5 hover:opacity-80 transition-opacity ${meta.cls}`} data-testid={`event-social-link-${i}`}>
                      <meta.Icon className="h-3.5 w-3.5" /> {meta.label}
                    </a>
                  );
                })}
              </div>
            )}
            {(ev.videos || []).filter((v) => v.url).map((v, i) => (
              <a key={i} href={v.url} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 border border-border rounded-lg px-3 py-2.5 hover:bg-slate-50 transition-colors" data-testid={`event-video-link-${i}`}>
                <PlayCircle className="h-6 w-6 text-red-500 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{v.text || "Video dekhें"}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{v.url}</p>
                </div>
              </a>
            ))}
          </div>
        )}

        {!regOpen && (
          <div className="bg-white border border-border rounded-2xl shadow-sm p-10 text-center space-y-3" data-testid="event-reg-closed">
            <CalendarCheck className="h-12 w-12 mx-auto text-amber-500" />
            <p className="font-heading text-lg font-bold" data-testid="event-reg-closed-msg">
              {ev.closed_message === "closed" ? "Registrations Are Currently Closed." : "Stay Tuned — Registrations Will Open Soon!"}
            </p>
          </div>
        )}

        {regOpen && (<>
        <div className="bg-white border border-border rounded-2xl shadow-sm p-6 space-y-4">
          <p className="font-heading font-semibold text-sm">Registration Form</p>

          {(ev.fields || []).map((f) => (
            <div key={f.key} className="space-y-1.5" data-testid={`field-wrap-${f.key}`}>
              <Label>{f.label}{f.required ? " *" : ""}</Label>
              {f.type === "text" && <Input value={data[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} data-testid={`field-${f.key}`} />}
              {f.type === "number" && <Input type="number" value={data[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} data-testid={`field-${f.key}`} />}
              {f.type === "date" && <Input type="date" value={data[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} data-testid={`field-${f.key}`} />}
              {f.type === "time" && <Input type="time" value={data[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} data-testid={`field-${f.key}`} />}
              {f.type === "longtext" && <textarea rows={3} value={data[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} className="w-full text-sm border border-input rounded-md px-3 py-2 resize-y" data-testid={`field-${f.key}`} />}
              {f.type === "dropdown" && (
                <Select value={data[f.key] || ""} onValueChange={(v) => setField(f.key, v)}>
                  <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{(f.options || []).filter(Boolean).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {f.type === "checkbox" && (
                <div className="space-y-1.5" data-testid={`field-${f.key}`}>
                  {(f.options || []).filter(Boolean).map((o) => {
                    const arr = Array.isArray(data[f.key]) ? data[f.key] : [];
                    const on = arr.includes(o);
                    return (
                      <label key={o} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`field-${f.key}-opt-${o.replace(/\s+/g, "-")}`}>
                        <input type="checkbox" checked={on} onChange={() => setField(f.key, on ? arr.filter((x) => x !== o) : [...arr, o])} className="h-4 w-4 accent-pink-600" />
                        {o}
                      </label>
                    );
                  })}
                </div>
              )}
              {f.type === "photo" && (
                <div className="flex items-center gap-2">
                  <label className={`inline-flex items-center gap-1.5 text-sm border rounded-md px-3 py-2 ${uploading[f.key] ? "pointer-events-none opacity-80 bg-cyan-50 text-cyan-700 border-cyan-200" : "cursor-pointer hover:bg-slate-50"}`} data-testid={`field-${f.key}`}>
                    {uploading[f.key] ? <><Loader2 className="h-4 w-4 animate-spin" data-testid={`field-${f.key}-uploading`} /> Uploading...</> : <><Upload className="h-4 w-4" /> {data[f.key] ? "Change" : "Upload"}</>}
                    <input type="file" accept="image/*" hidden disabled={!!uploading[f.key]} onChange={(e) => { pickPhoto(e.target.files[0], f.key); e.target.value = ""; }} data-testid={`field-${f.key}-file`} />
                  </label>
                  {data[f.key] && photoCache[data[f.key]] && (
                    <>
                      <img src={photoCache[data[f.key]]} alt="" className="h-12 w-12 rounded object-cover border cursor-pointer" onClick={() => setCropState({ key: f.key, src: photoCache[data[f.key]] })} data-testid={`field-${f.key}-thumb`} />
                      <button type="button" onClick={() => setCropState({ key: f.key, src: photoCache[data[f.key]] })} className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 border border-cyan-200 bg-cyan-50 rounded-md px-2.5 py-1.5 hover:bg-cyan-100" data-testid={`field-${f.key}-adjust`}><Crop className="h-3.5 w-3.5" /> Adjust</button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {ev.payment_required && (
          <div className="bg-white border border-border rounded-2xl shadow-sm p-6 space-y-3" data-testid="event-payment-block">
            <p className="font-heading font-semibold text-sm">Payment</p>
            {(ev.payment_categories || []).length > 0 && (
              <div className="space-y-1.5">
                <Label>Payment Category *</Label>
                <Select value={catId} onValueChange={setCatId}>
                  <SelectTrigger data-testid="event-pay-cat"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{ev.payment_categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — ₹{c.amount}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {selectedCat && <p className="text-sm">Amount to pay: <b className="text-cyan-700">₹{selectedCat.amount}</b></p>}
            {ev.payment_note && <p className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 whitespace-pre-wrap" data-testid="event-pay-note">{ev.payment_note}</p>}
            {ev.payment_qr && <img src={ev.payment_qr} alt="Pay QR" className="h-44 w-44 mx-auto border rounded-lg" data-testid="event-pay-qr" />}
            {ev.payment_number && <p className="text-sm text-center">Pay to: <b className="text-cyan-700">{ev.payment_number}</b></p>}
            <div className="space-y-1.5">
              <Label>Payment Screenshot *</Label>
              <div className="flex items-center gap-2">
                <label className={`inline-flex items-center gap-1.5 text-sm border rounded-md px-3 py-2 ${uploading.__pay ? "pointer-events-none opacity-80 bg-cyan-50 text-cyan-700 border-cyan-200" : "cursor-pointer hover:bg-slate-50"}`} data-testid="event-pay-shot">
                  {uploading.__pay ? <><Loader2 className="h-4 w-4 animate-spin" data-testid="event-pay-shot-uploading" /> Uploading...</> : <><Upload className="h-4 w-4" /> {payShot ? "Change" : "Upload"}</>}
                  <input type="file" accept="image/*" hidden disabled={!!uploading.__pay} onChange={(e) => { uploadPhoto(e.target.files[0], "__pay"); e.target.value = ""; }} data-testid="event-pay-shot-file" />
                </label>
                {payShot && photoCache[payShot] && <img src={photoCache[payShot]} alt="" className="h-12 w-12 rounded object-cover border" />}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Transaction / UPI Reference (optional)</Label>
              <Input value={payRef} onChange={(e) => setPayRef(e.target.value)} data-testid="event-pay-ref" placeholder="e.g. UPI ref no." />
            </div>
          </div>
        )}

        {ev.pass_download_enabled && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-2xl shadow-sm p-5 space-y-2" data-testid="event-passkey-block">
            <p className="font-heading font-semibold text-sm flex items-center gap-1.5 text-indigo-800"><KeyRound className="h-4 w-4" /> Apni Pass Key banayein *</p>
            <p className="text-[11px] text-indigo-700/80">Ye 4 ya 6 digit key <b>zaroori</b> hai — isi se baad me apna pass dekh/download kar paayenge. Key yaad rakhein.</p>
            <Input value={myKey} onChange={(e) => setMyKey(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="4 ya 6 digit key" className="max-w-xs" data-testid="event-mykey-input" />
          </div>
        )}

        <Button onClick={submit} disabled={busy || Object.values(uploading).some(Boolean)} data-testid="event-submit-btn" className="w-full h-11 bg-pink-600 hover:bg-pink-700 text-white font-semibold">
          {busy ? "Submitting..." : "Submit for Approval"}
        </Button>
        </>)}

        {ev.pass_download_enabled && <PassDownloadDialog open={passDlOpen} onClose={() => setPassDlOpen(false)} token={token} ev={ev} />}

        {zoomImg && (
          <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" data-testid="event-zoom-overlay" onClick={() => setZoomImg(null)}>
            <div className="absolute top-4 right-4 flex gap-2 z-10" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setZoom((z) => Math.max(1, z - 0.5))} className="bg-white/15 hover:bg-white/25 text-white rounded-full p-2.5" data-testid="event-zoom-out"><ZoomOut className="h-5 w-5" /></button>
              <button onClick={() => setZoom((z) => Math.min(4, z + 0.5))} className="bg-white/15 hover:bg-white/25 text-white rounded-full p-2.5" data-testid="event-zoom-in"><ZoomIn className="h-5 w-5" /></button>
              <button onClick={() => setZoomImg(null)} className="bg-white/15 hover:bg-white/25 text-white rounded-full p-2.5" data-testid="event-zoom-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="max-w-full max-h-[85vh] overflow-auto p-4" onClick={(e) => e.stopPropagation()}>
              <img src={zoomImg} alt="" onClick={() => setZoom((z) => (z >= 3 ? 1 : z + 1))} style={{ transform: `scale(${zoom})`, transformOrigin: "center top" }} className="max-w-full rounded-lg transition-transform duration-200 cursor-zoom-in" data-testid="event-zoom-img" />
            </div>
          </div>
        )}
      </div>
      <PhotoCropDialog open={!!cropState} src={cropState?.src} onClose={() => setCropState(null)} onDone={onCropDone} />
    </div>
  );
}
